import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { ChildTileData, CMSViewMode } from '../../types/CMSTypes';
import { getCategoryDetails, buildCategoryUrl } from '../../api/cmsApi';

interface CMSConsumerCollectionsGridProps {
  collectionsData: ChildTileData[];
  applicationName?: string;
  marginTop?: number;
  marginBottom?: number;
  viewMode?: CMSViewMode;
}

const CMSConsumerCollectionsGrid: React.FC<CMSConsumerCollectionsGridProps> = ({
                                                                                 collectionsData,
                                                                                 applicationName = '',
                                                                                 marginTop = 5,
                                                                                 marginBottom = 5,
                                                                                 viewMode = 'live'
                                                                               }) => {
  const navigate = useNavigate();

  // Handle navigation for collection items
  const handleCollectionClick = async (item: ChildTileData) => {
    try {
      // Build the collection URL using CMS category data
      const categoryResponse = await getCategoryDetails(item.category_id, viewMode);
      if (categoryResponse.success && categoryResponse.cms_category) {
        const categoryUrl = buildCategoryUrl(categoryResponse.cms_category);
        navigate(categoryUrl);
      } else {
        // Fallback to manual URL building
        if (item.item_group && item.application && item.collection) {
          const url = `/item_group/${encodeURIComponent(item.application)}/collection/${encodeURIComponent(item.collection)}`;
          navigate(url);
        }
      }
    } catch (error) {
      console.error('Error navigating to collection:', error);
      // Final fallback - use the application/collection structure
      if (item.item_group && item.application && item.collection) {
        const url = `/item_group/${encodeURIComponent(item.application)}/collection/${encodeURIComponent(item.collection)}`;
        navigate(url);
      }
    }
  };

  // Capitalize application name for display
  const capitalizeApplicationName = (name: string) => {
    return name
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Show missing content if no data
  if (!collectionsData || collectionsData.length === 0) {
    return <MissingContent sectionName="Collections Section" />;
  }

  // Filter for published collections only (consumer safety)
  const publishedCollections = collectionsData
    .filter(item => item.is_published)
    .sort((a, b) => {
      // Sort by collection name alphabetically
      const nameA = a.collection || a.category_name || '';
      const nameB = b.collection || b.category_name || '';
      return nameA.localeCompare(nameB);
    });

  return (
    <Box
      sx={{
        mt: marginTop,
        mb: marginBottom,
        ml: { xs: '5%', lg: '5rem' },
        mr: { xs: '5%', lg: '5rem' },
      }}
    >
      {/* Header */}
      <Typography
        variant="h4"
        mb={4}
        fontWeight="400"
        fontSize={'56px'}
      >
        {`${capitalizeApplicationName(applicationName)} Collections`}
      </Typography>

      {/* Collections Grid */}
      <Grid container columnSpacing={5} rowSpacing={8}>
        {publishedCollections.map((item, index) => (
          <Grid key={item.category_id || index} item xs={6} sm={3} md={2}>
            <Box
              sx={{
                cursor: 'pointer',
                height: {
                  xs: '150px',
                  lg: '276px',
                },
                width: { xs: '100%', lg: '100%' },
                '&:hover img': {
                  transform: 'scale(1.03)',
                },
                overflow: 'hidden',
              }}
              onClick={() => handleCollectionClick(item)}
            >
              <img
                src={item.tile_display_media_single_link || '/placeholder-image.jpg'}
                style={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                }}
                alt={item.collection || item.category_name || 'Collection'}
              />
            </Box>

            {/* Collection Name */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
                flexDirection: 'column',
              }}
            >
              <Typography
                mt={1}
                fontWeight={'600'}
                textTransform={'capitalize'}
              >
                {item.collection || item.category_name}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CMSConsumerCollectionsGrid;